import { CreditCard } from 'lucide-react'
import { motion, useReducedMotion } from 'framer-motion'

import { Button } from '@/components/ui/button'
import { useSubscriptionContext } from '@/app/providers/subscription-provider'
import { useCreatePortal } from '@/hooks/use-subscription'
import type { PaymentIssue, PaymentIssueKind } from '@/services/billing-api'

const COPY: Record<PaymentIssueKind, { title: string; body: string }> = {
  payment_failed: {
    title: 'Ödemeni alamadık',
    body: 'Erişimini kaybetmemek için ödeme yöntemini güncelle.',
  },
  card_expiring: {
    title: 'Kartının süresi doluyor',
    body: 'Yenilemeden önce güncelle, kesinti olmasın.',
  },
}

/**
 * Yan menünün altında (hesap satırının üstünde) görünen küçük ödeme uyarısı (#981).
 * Backend `payment_issue` alanı null ise (anahtar kapalı, sorun yok, eski backend) hiçbir şey çizmez.
 * Renkler: koyu temada amber-400, açık temada amber-800 (`data-theme` ile; sistem tercihine bağlı değil).
 */
export function PaymentIssueCard() {
  const { paymentIssue } = useSubscriptionContext()
  // Mutasyon (QueryClient) yalnız kart gerçekten çizilirken kurulur.
  return paymentIssue ? <PaymentIssueCardBody issue={paymentIssue} /> : null
}

function PaymentIssueCardBody({ issue }: { issue: PaymentIssue }) {
  const portal = useCreatePortal()
  const reduceMotion = useReducedMotion()
  const copy = COPY[issue.kind]

  return (
    <motion.div
      data-testid="payment-issue-card"
      data-kind={issue.kind}
      initial={reduceMotion ? false : { opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      transition={reduceMotion ? { duration: 0 } : { duration: 0.22, ease: 'easeOut' }}
      className="mb-2 overflow-hidden"
    >
      <div
        role="status"
        className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-amber-400 [[data-theme=light]_&]:border-amber-600/40 [[data-theme=light]_&]:bg-amber-50 [[data-theme=light]_&]:text-amber-800"
      >
        <div className="flex items-center gap-2">
          <CreditCard className="h-4 w-4 shrink-0" aria-hidden="true" />
          <p className="text-sm font-semibold leading-tight">{copy.title}</p>
        </div>
        <p className="mt-1 text-xs leading-snug opacity-90">{copy.body}</p>
        <Button
          size="sm"
          variant="outline"
          className="mt-2 w-full border-amber-500/40 bg-transparent text-amber-400 hover:bg-amber-500/15 hover:text-amber-300 [[data-theme=light]_&]:border-amber-700/50 [[data-theme=light]_&]:text-amber-800 [[data-theme=light]_&]:hover:bg-amber-100 [[data-theme=light]_&]:hover:text-amber-900"
          onClick={() => portal.mutate()}
          disabled={portal.isPending}
        >
          Ödeme yöntemini güncelle
        </Button>
      </div>
    </motion.div>
  )
}
